import * as React from 'react';
import { CircleX, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
    caretPosForNationalDigitIndex,
    digitsOnly,
    formatRussianMobileNumberMasked,
} from '../../utils';

export interface ProfileCreateFormProps {
    onSubmit: (telephone: string) => void;
    disabled?: boolean;
    /** E.164 (+79099993086) — applied when set/changed (demo tour autofill). */
    prefillTelephone?: string;
    registerTourTarget?: (id: string, el: HTMLElement | null) => void;
}

function nationalDigitsFromE164(value: string): string {
    let d = digitsOnly(value);
    if (d.length === 11 && (d.startsWith('7') || d.startsWith('8'))) d = d.slice(1);
    return d.slice(0, 10);
}

export function ProfileCreateForm({
    onSubmit,
    disabled,
    prefillTelephone,
    registerTourTarget,
}: ProfileCreateFormProps) {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [telephoneNationalDigits, setTelephoneNationalDigits] = React.useState('');

    const formRef = React.useCallback(
        (el: HTMLDivElement | null) => {
            registerTourTarget?.('phone-form', el);
        },
        [registerTourTarget],
    );

    const addButtonRef = React.useCallback(
        (el: HTMLButtonElement | null) => {
            registerTourTarget?.('add-profile', el);
        },
        [registerTourTarget],
    );

    React.useEffect(() => {
        if (!prefillTelephone) return;
        const national = nationalDigitsFromE164(prefillTelephone);
        if (national.length === 10) {
            setTelephoneNationalDigits(national);
        }
    }, [prefillTelephone]);

    const telephoneDisplay = React.useMemo(
        () => (telephoneNationalDigits ? `+7 ${formatRussianMobileNumberMasked(telephoneNationalDigits)}` : ''),
        [telephoneNationalDigits]
    );

    const setCaret = React.useCallback((pos: number) => {
        const el = inputRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
            try {
                el.setSelectionRange(pos, pos);
            } catch {
                // ignore
            }
        });
    }, []);

    return (
        <div
            ref={formRef}
            className="rounded-l "
            data-tour-target="phone-form"
        >
            <div className="mb-2 text-h3 font-medium">Загрузка данных клиента</div>
            <div className="text-body1">
                Введите номер телефона, чтобы найти клиента в базе и сформировать новую анкету.
            </div>

            <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-end">
                <div className="flex-1">
                    <div className="text-body2 text-neutral-text-500">Телефон клиента</div>
                    <Input
                        ref={inputRef}
                        inputMode="tel"
                        autoComplete="tel"
                        value={telephoneDisplay}
                        onKeyDown={(e) => {
                            const el = e.currentTarget;
                            const start = el.selectionStart ?? 0;
                            const end = el.selectionEnd ?? 0;
                            if (start !== end) return;

                            const isBackspace = e.key === 'Backspace';
                            const isDelete = e.key === 'Delete';
                            if (!isBackspace && !isDelete) return;

                            let digitsLeftOfCaret = digitsOnly(telephoneDisplay.slice(0, start)).length;
                            if (telephoneDisplay.startsWith('+7') && digitsLeftOfCaret > 0) {
                                digitsLeftOfCaret = Math.max(0, digitsLeftOfCaret - 1);
                            }

                            const digitIndexToRemove = isBackspace ? digitsLeftOfCaret - 1 : digitsLeftOfCaret;

                            if (digitIndexToRemove < 0) {
                                if (isBackspace) {
                                    setTelephoneNationalDigits('');
                                    setCaret(0);
                                    e.preventDefault();
                                }
                                return;
                            }

                            if (digitIndexToRemove >= telephoneNationalDigits.length) {
                                return;
                            }

                            const nextDigits = telephoneNationalDigits
                                .split('')
                                .filter((_, idx) => idx !== digitIndexToRemove)
                                .join('');
                            setTelephoneNationalDigits(nextDigits);

                            const nextDisplay = nextDigits ? `+7 ${formatRussianMobileNumberMasked(nextDigits)}` : '';
                            const nextDigitsLeft = isBackspace ? digitIndexToRemove : digitsLeftOfCaret;
                            const nextPosInMasked = nextDisplay
                                ? caretPosForNationalDigitIndex(nextDisplay, nextDigitsLeft + 1)
                                : 0;
                            setCaret(nextPosInMasked);
                            e.preventDefault();
                        }}
                        onChange={(e) => {
                            const raw = e.target.value;
                            const caret = e.target.selectionStart ?? raw.length;
                            let digitsLeft = digitsOnly(raw.slice(0, caret)).length;
                            if (digitsLeft > 0 && raw.startsWith('+7')) digitsLeft = Math.max(0, digitsLeft - 1);

                            let d = digitsOnly(raw);
                            if (raw.startsWith('+7') && d.startsWith('7')) d = d.slice(1);
                            if (d.length > 10) d = d.slice(0, 10);
                            setTelephoneNationalDigits(d);

                            const nextDisplay = d ? `+7 ${formatRussianMobileNumberMasked(d)}` : '';
                            const nextPos = nextDisplay ? caretPosForNationalDigitIndex(nextDisplay, digitsLeft + 1) : 0;
                            setCaret(nextPos);
                        }}
                        placeholder="+7 (___) ___-__-__"
                        className="mt-2 h-11 rounded-m"
                        disabled={disabled}
                    />
                </div>

                <Button
                    ref={addButtonRef}
                    type="button"
                    data-tour-target="add-profile"
                    onClick={() =>
                        onSubmit(telephoneNationalDigits.length === 10 ? `+7${telephoneNationalDigits}` : '')

                    }
                    disabled={disabled || telephoneNationalDigits.length < 10}
                >
                    <Plus strokeWidth={2.5} className="h-4 w-4" />
                    <span className="text-body1 font-medium">Добавить</span>
                </Button>
            </div>
        </div>
    );
}

export interface ProcessingStatusCardProps {
    title: string;
    subtitle: string;
    className?: string;
}

export function ProcessingStatusCard({
    title,
    subtitle,
    className,
}: ProcessingStatusCardProps) {
    return (
        <div
            className={cn("rounded-l p-5 border border-neutral-border-300", className)}
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-2">
                    <div className="text-body2 text-h3 font-medium">{title}</div>
                    <div className="text-body2 text-body1 text-neutral-text-500">
                        {subtitle}
                    </div>
                </div>
                <Loader2 stroke="var(--color-primary-500)" className="h-5 w-5 animate-spin" />
            </div>
        </div>
    );
}

export interface ProfileProcessingCardProps {
    telephone?: string;
}

export function ProfileProcessingCard({
    telephone,
}: ProfileProcessingCardProps = {}) {
    let formattedTelephone = ""
    if (telephone) {
        formattedTelephone = ` +7 ${formatRussianMobileNumberMasked(telephone!.slice(2))}`;
    }
    return (
        <ProcessingStatusCard
            className="mb-8"
            title={`Формирование анкеты клиента${formattedTelephone}...`}
            subtitle="Пожалуйста, подождите, мы загружаем историю заказов и аудиозаписи из базы данных."
        />
    );
}

export interface ProfileErrorBannerProps {
    message: string;
    onDismiss: () => void;
}

export function ProfileErrorBanner({ message, onDismiss }: ProfileErrorBannerProps) {
    return (
        <div className="rounded-m border border-state-negative p-5" role="alert" aria-live="assertive">
            <div className="flex items-center justify-between gap-4">
                <div className="text-h3 font-medium">Не удалось сформировать анкету</div>
                <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={onDismiss}
                    aria-label="Закрыть"
                    className="h-auto p-0"
                >
                    <CircleX className="h-5 w-5 hover:opacity-80 transition-opacity" color="var(--color-state-negative)" />
                </IconButton>
            </div>
            <div className="mt-2 text-body2 text-state-negative">{message}</div>
        </div>
    );
}
